/**
 * Servicio para consultar pedidos y extraer guías para el proceso manual de Envía.com
 */
import axiosInstance from '../../api/axiosInstance'
import type { OrderWithGuide } from '../../types/cron/enviaManual'
import type { Order } from '../../types/order'
import {
  getEnviaComManualCarrierCanonical,
  isAllowedEnviaComManualCarrier,
} from '../../utils/enviaComCarrierUtils'

type OrderListRow = Order & {
  transportadora?: string
  extraDetails?: { transportadora?: string }
}

interface ListResponse {
  data: Order[]
  meta: {
    currentPage: number
    totalPages: number
    totalItems: number
    statistics?: any
  }
}

export class EnviaManualService {
  private static readonly TARGET_STORES = [
    'Artiq',
    'Bigcenter',
    'Distrito Cafetero',
    'Baristico',
    'Mercado Libre',
  ]
  private static readonly TARGET_STATES = ['enviado', 'on_route']
  /** Valores para query /list (coinciden con arrays.js cuando aplica) */
  private static readonly TARGET_TRANSPORTADORAS_QUERY = [
    'inter_rapidisimo',
    'deprisa',
    'coordinadora',
    'noventa9minutos',
  ]
  private static readonly LIMIT_PER_PAGE = 100

  private static buildListQueryParams(page: number): string {
    const stores = this.TARGET_STORES.join(',')
    const states = this.TARGET_STATES.join(',')
    const transportadoras = this.TARGET_TRANSPORTADORAS_QUERY.join(',')
    return (
      `page=${page}&limit=${this.LIMIT_PER_PAGE}` +
      `&states=${states}&stores=${stores}&transportadora=${transportadoras}`
    )
  }

  private static getOrderTransportadora(order: OrderListRow): string | undefined {
    const raw =
      order.transportadora ?? order.extraDetails?.transportadora ?? undefined
    return typeof raw === 'string' ? raw : undefined
  }

  /**
   * Obtener todos los pedidos con guías de las tiendas y estados especificados
   * Consulta todas las páginas necesarias
   */
  static async getAllOrdersWithGuides(
    onProgress?: (progress: {
      currentPage: number
      totalPages: number
      ordersFetched: number
    }) => void
  ): Promise<OrderWithGuide[]> {
    const token = localStorage.getItem('accessToken')
    if (!token) {
      throw new Error('No se encontró token de autenticación')
    }

    const allOrders: OrderWithGuide[] = []
    let currentPage = 1
    let totalPages = 1

    try {
      // Primera consulta para obtener totalPages
      const firstResponse = await axiosInstance.get<ListResponse>(
        `/list?${this.buildListQueryParams(currentPage)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      const firstData = firstResponse.data.data
      totalPages = firstResponse.data.meta.totalPages

      // Extraer guías de la primera página
      const firstPageGuides = this.extractGuidesFromOrders(firstData)
      allOrders.push(...firstPageGuides)

      // Notificar progreso
      if (onProgress) {
        onProgress({
          currentPage: 1,
          totalPages,
          ordersFetched: firstData.length,
        })
      }

      // Si hay más páginas, consultarlas todas
      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page++) {
          const response = await axiosInstance.get<ListResponse>(
            `/list?${this.buildListQueryParams(page)}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )

          const pageData = response.data.data
          const pageGuides = this.extractGuidesFromOrders(pageData)
          allOrders.push(...pageGuides)

          // Notificar progreso
          if (onProgress) {
            onProgress({
              currentPage: page,
              totalPages,
              ordersFetched: allOrders.length,
            })
          }
        }
      }

      return allOrders
    } catch (error: any) {
      console.error('Error obteniendo pedidos con guías:', error)
      throw new Error(
        error.response?.data?.message ||
          `Error al obtener pedidos: ${error.message}`
      )
    }
  }

  /**
   * Extraer guías de pedidos Envía.com (carriers integrados), excluyendo Envía/Colvanes directo
   * - Transportadora: Inter Rapidísimo, Deprisa, Coordinadora, 99 Minutos (mayúsculas/tildes OK)
   * - Excluye carrier "envia" / "Envía"
   * - Excluye guías Colvanes (prefijo "03")
   */
  private static extractGuidesFromOrders(orders: OrderListRow[]): OrderWithGuide[] {
    const guides: OrderWithGuide[] = []

    for (const order of orders) {
      if (!order.guia || !order.orders_id) {
        continue
      }

      const guiaTrimmed = order.guia.trim()
      if (guiaTrimmed.startsWith('03')) {
        continue
      }

      const transportadoraRaw = this.getOrderTransportadora(order)
      if (!isAllowedEnviaComManualCarrier(transportadoraRaw)) {
        continue
      }

      guides.push({
        guia: guiaTrimmed,
        orders_id: order.orders_id,
        store_name: order.store_name,
        id: order.id,
        transportadora: transportadoraRaw,
        carrierCanonical: getEnviaComManualCarrierCanonical(transportadoraRaw) ?? undefined,
      })
    }

    return guides
  }
}

